/* import __COLOCATED_TEMPLATE__ from './generic-before-after-details.hbs'; */
/* RESPONSIBLE TEAM: team-app-security */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { isEmpty, isNone } from '@ember/utils';
import { isEqual } from 'underscore';
import type AppAdminEvent from 'embercom/models/app-admin-event';
import type IntlService from 'ember-intl/services/intl';
import toSentence from 'embercom/lib/to-sentence';

interface FieldChange {
  key: string;
  before: any;
  after: any;
  hasChanged: boolean;
  formattedBefore: string;
  formattedAfter: string;
}

interface GenericBeforeAfterDetailsSignature {
  Args: {
    event: AppAdminEvent;
  };
}
export default class GenericBeforeAfterDetails extends Component<GenericBeforeAfterDetailsSignature> {
  @service declare intl: IntlService;

  @tracked showModal = false;

  get hasBeforeAfterData(): boolean {
    let { before, after } = this.args.event.value || {};
    return before !== undefined && after !== undefined && !isEqual(before, after);
  }

  get beforeData() {
    return this.args.event.value?.before;
  }

  get afterData() {
    return this.args.event.value?.after;
  }

  get fieldChanges(): FieldChange[] {
    let before = this.beforeData;
    let after = this.afterData;

    if (!before || !after) {
      return [];
    }

    if (typeof before !== 'object' || typeof after !== 'object') {
      let hasChanged = !isEqual(before, after);
      return hasChanged
        ? [
            {
              key: this.intl.t('table-cells.admin-events.generic-before-after-details.value'),
              before,
              after,
              hasChanged,
              formattedBefore: this.formatValue(before),
              formattedAfter: this.formatValue(after),
            },
          ]
        : [];
    }

    let allKeys = new Set([...Object.keys(before), ...Object.keys(after)]);

    return Array.from(allKeys)
      .map((key) => {
        let beforeValue = before[key];
        let afterValue = after[key];
        let hasChanged = !this.valuesAreEqual(beforeValue, afterValue);

        return {
          key: this.formatPropertyKey(key),
          before: beforeValue,
          after: afterValue,
          hasChanged,
          formattedBefore: this.formatValue(beforeValue),
          formattedAfter: this.formatValue(afterValue),
        };
      })
      .filter((change) => change.hasChanged);
  }

  private valuesAreEqual(valueA: any, valueB: any): boolean {
    // Normalize null/undefined to false for boolean comparison
    let normalizedA = isNone(valueA) ? false : valueA;
    let normalizedB = isNone(valueB) ? false : valueB;
    return isEqual(normalizedA, normalizedB);
  }

  private formatPropertyKey(key: string): string {
    // Convert snake_case to Title Case (e.g., "authorized_sign_in_methods" -> "Authorized Sign In Methods")
    return key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  }

  private formatValue(value: any): string {
    if (isNone(value)) {
      return this.intl.t('table-cells.admin-events.generic-before-after-details.none');
    }

    if (typeof value === 'boolean') {
      return value
        ? this.intl.t('table-cells.admin-events.generic-before-after-details.yes')
        : this.intl.t('table-cells.admin-events.generic-before-after-details.no');
    }

    if (Array.isArray(value)) {
      return isEmpty(value)
        ? this.intl.t('table-cells.admin-events.generic-before-after-details.empty')
        : toSentence(value, 'and');
    }

    if (typeof value === 'object') {
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return String(value);
      }
    }

    return String(value);
  }

  @action
  toggleModal(): void {
    this.showModal = !this.showModal;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'TableCells::AdminEvents::GenericBeforeAfterDetails': typeof GenericBeforeAfterDetails;
  }
}
